import {DictionaryEntries, DictionaryLocale} from '@web-libs/core/internationalization/dictionary.types';

export function isValidDate(dateString: string) {
    // Check if the string matches the TT.MM.YYYY format using a regex
    const regex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
    const matches = dateString.match(regex);

    try {
        if (matches) {
            // Extract day, month, and year from the regex matches
            const day = parseInt(matches[1],
                                 10);
            const month = parseInt(matches[2],
                                   10) - 1; // Months are 0-indexed in JavaScript
            const year = parseInt(matches[3],
                                  10);

            // Create a new Date object
            const date = new Date(year,
                                  month,
                                  day);

            // Check if the created date is valid and matches the provided day, month, and year
            if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
                return true;
            }
        }
    } catch (e) {
        return false;
    }

    return false;
}

export function isValidPhoneNumber(phoneNumber: string) {
    // Check if the string matches the +49 123 4567890 format using a regex
    const regex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
    return regex.test(phoneNumber);

}

export function formatToYYYYMMDD(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2,
                                                    '0'); // Months are 0-based, so we add 1
    const dd = String(date.getDate()).padStart(2,
                                               '0');
    return `${yyyy}-${mm}-${dd}`;
}

export function dateStringToUTCDate(date: string): Date {
    return new Date(date.endsWith('Z') ? date : date + 'Z');
}

export function formatToDDMMYYYY(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2,
                                                    '0'); // Months are 0-based, so we add 1
    const dd = String(date.getDate()).padStart(2,
                                               '0');
    return `${dd}.${mm}.${yyyy}`;
}

export function formatDateBeautiful(startDate: Date,
                                    endDate?: Date,
                                    skipTime?: boolean,
                                    dict?: DictionaryEntries,) {
    const dictDate = dict?.date;
    const dictWeekday = dictDate?.weekday;
    const dictMonth = dictDate?.month;
    const days = dict ? [
        dictWeekday?.sunday,
        dictWeekday?.monday,
        dictWeekday?.tuesday,
        dictWeekday?.wednesday,
        dictWeekday?.thursday,
        dictWeekday?.friday,
        dictWeekday?.saturday
    ] : ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    const months = dict ? [
            '',
            dictMonth?.january,
            dictMonth?.february,
            dictMonth?.march,
            dictMonth?.april,
            dictMonth?.may,
            dictMonth?.june,
            dictMonth?.july,
            dictMonth?.august,
            dictMonth?.september,
            dictMonth?.october,
            dictMonth?.november,
            dictMonth?.december
        ]
        : ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

    const startDay = days[startDate.getDay()];
    const startMonth = months[startDate.getMonth() + 1];
    const startTime = startDate.getHours().toString().padStart(2,
                                                               '0') + ':' + startDate.getMinutes().toString().padStart(2,
                                                                                                                       '0');

    if (endDate) {
        const endTime = endDate.getHours().toString().padStart(2,
                                                               '0') + ':' + endDate.getMinutes().toString().padStart(2,
                                                                                                                     '0');
        if (skipTime) {
            return `${startDay}, ${startDate.getDate()}. ${startMonth}`;
        }
        return `${startDay}, ${startDate.getDate()}. ${startMonth} · ${startTime} - ${endTime}`;
    } else {
        if (skipTime) {
            return `${startDay}, ${startDate.getDate()}. ${startMonth}`;
        }
        return `${startDay}, ${startDate.getDate()}. ${startMonth} · ${startTime}`;
    }
}

export function formatDateRelativeToToday(date: Date,
                                          dict: DictionaryEntries): string {
    const dictDate = dict.date;
    const dictWeekday = dictDate.weekday;
    const dictShortWeekday = dictDate.shortWeekday;
    const now = new Date();
    const today = new Date(now.getFullYear(),
                           now.getMonth(),
                           now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const dayOfWeek = date.getDay();
    const dayOfWeekNow = now.getDay();

    // Format time
    const time = date.toLocaleTimeString('de-DE',
                                         {hour: '2-digit', minute: '2-digit'});

    if (date >= today) {
        // Same day
        return time;
    } else if (date >= yesterday) {
        // Yesterday
        return `${dictDate.yesterday}, ${time}`;
    } else if (date >= new Date(now.setDate(now.getDate() - 7))) {
        // Same week
        const days = [
            dictShortWeekday.sunday,
            dictShortWeekday.monday,
            dictShortWeekday.tuesday,
            dictShortWeekday.wednesday,
            dictShortWeekday.thursday,
            dictShortWeekday.friday,
            dictShortWeekday.saturday];
        return `${days[dayOfWeek]} ${time}`;
    } else {
        // More than a week ago
        const year = date.getFullYear().toString().slice(-2);
        const month = (date.getMonth() + 1).toString().padStart(2,
                                                                '0');
        const day = date.getDate().toString().padStart(2,
                                                       '0');
        return `${day}.${month}.${year} ${time}`;
    }
}

export function getFirstDateOfWeek(date: Date) {
    let day = date.getDay();
    let diff = date.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
    return new Date(date.setDate(diff));
}

export function formatDayDateRelativeToToday(date: Date,
                                             dict: DictionaryEntries): string {
    const dictDate = dict.date;
    const dictWeekday = dictDate.weekday;
    const dictShortWeekday = dictDate.shortWeekday;
    const now = new Date();
    const today = new Date(now.getFullYear(),
                           now.getMonth(),
                           now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const dayOfWeek = date.getDay();

    if (date >= today) {
        // Same day
        return dictDate.today;
    } else if (date >= yesterday) {
        return dictDate.yesterday;
    } else if (date >= new Date(now.setDate(now.getDate() - 7))) {
        // Same week
        const days = [
            dictShortWeekday.sunday,
            dictShortWeekday.monday,
            dictShortWeekday.tuesday,
            dictShortWeekday.wednesday,
            dictShortWeekday.thursday,
            dictShortWeekday.friday,
            dictShortWeekday.saturday];
        return `${days[dayOfWeek]}`;
    } else {
        // More than a week ago
        const year = date.getFullYear().toString().slice(-2);
        const month = (date.getMonth() + 1).toString().padStart(2,
                                                                '0');
        const day = date.getDate().toString().padStart(2,
                                                       '0');
        return `${day}.${month}.${year}`;
    }
}


export function formatTime(date: Date): string {
    return date.toLocaleTimeString('de-DE',
                                   {hour: '2-digit', minute: '2-digit'});
}

export function formatTimeFromTo(startDate: Date,
                                 endDate?: Date): string {
    const startTime = formatTime(startDate);
    if (!endDate) {
        return startTime;
    }
    const endTime = formatTime(endDate);
    return `${startTime} - ${endTime}`;
}

export const sortByDateAsc = <T>(array: T[],
                                 getDate: (arrayElement: T) => Date): T[] => {
    return array.toSorted((a,
                           b) => getDate(a).getTime() - getDate(b).getTime());
};

export const sortByDateDesc = <T>(array: T[],
                                  getDate: (arrayElement: T) => Date): T[] => {
    return array.toSorted((a,
                           b) => getDate(b).getTime() - getDate(a).getTime());
};

export const isSameDay = (date1: Date,
                          date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear()
        && date1.getMonth() === date2.getMonth()
        && date1.getDate() === date2.getDate();
};

export function toYYYYMMDDHHMMString(date: Date,
                                     hhmm: string) {
    return formatToYYYYMMDD(date) + 'T' + hhmm;
}

export function formatDuration(_seconds: number) {
    // Convert milliseconds to seconds
    const totalSeconds = Math.floor(_seconds);

    // Calculate minutes and seconds
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    // Format time as mm:ss
    return `${String(minutes).padStart(2,
                                       '0')}:${String(seconds).padStart(2,
                                                                        '0')}`;
}

/**
 *  Formats a date range in a beautiful way. Example: "Montag, 12. Oktober · 10:00 - 12:00"
 */
export function formatDateRange(startDate: Date,
                                endDate?: Date,
                                lang?: DictionaryLocale): string {
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit'
    };

    const startDateFormat = startDate.toLocaleDateString(lang,
                                                         options);
    const startTimeFormat = startDate.toLocaleTimeString(lang,
                                                         timeOptions);
    if (!endDate) {
        return `${startDateFormat} · ${startTimeFormat}`;
    }
    const endTimeFormat = endDate.toLocaleTimeString(lang,
                                                     timeOptions);

    if (startDate.toDateString() === endDate.toDateString()) {
        return `${startDateFormat} · ${startTimeFormat} - ${endTimeFormat}`;
    } else {
        const endDateFormat = endDate.toLocaleDateString(lang,
                                                         options);
        return `${startDateFormat} · ${startTimeFormat} - ${endDateFormat} ${endTimeFormat}`;
    }
}
